import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiExternalLink } from 'react-icons/fi';

import { Shareholder } from '../../../../../services/Interfaces/IManagemenntService';
import { DocumentsCompany } from '../..';

interface DocumentsListProps {
  documents: DocumentsCompany | undefined;
  changeOpen: (shareholderSlug: string, documentSlug: string, documentUrl: string) => void;
}

const DocumentsList: React.FC<DocumentsListProps> = ({ documents, changeOpen }) => {
  const { t } = useTranslation();
  
  // Função para obter badge do status
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'approved': { bg: 'bg-green-100', text: 'text-green-800', label: t('others.othApproved') || 'Approved' },
      'pending': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: t('others.othPending') || 'Pending' },
      'rejected': { bg: 'bg-red-100', text: 'text-red-800', label: t('others.othNotApproved') || 'Not Approved' },
      'reproved': { bg: 'bg-red-100', text: 'text-red-800', label: t('others.othNotApproved') || 'Not Approved' },
      'reviewing': { bg: 'bg-blue-100', text: 'text-blue-800', label: t('others.othReviewing') || 'Reviewing' },
      'not approved': { bg: 'bg-red-100', text: 'text-red-800', label: t('others.othNotApproved') || 'Not Approved' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    
    return (
      <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  // Função para formatar data
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };

  // Função para obter ícone do tipo de documento
  const getDocumentIcon = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'pdf':
        return (
          <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'image':
      case 'jpg':
      case 'png':
        return (
          <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
    }
  };

  const formatDocumentDescription = (description?: string, personName?: string) => {
    if (!description) {
      return personName ? `${personName} ${t('labels.lblNationalIDPlain') || 'National ID'}` : t('labels.lblNationalIDPlain') || 'National ID';
    }

    return description
      .replace(/local document/gi, t('labels.lblNationalIDPlain') || 'National ID')
      .replace(/National ID/gi, t('labels.lblNationalIDPlain') || 'National ID')
      .replace(/Driver licence/gi, t('labels.lblDriverLicence') || 'Driver licence')
      .replace(/Driver license/gi, t('labels.lblDriverLicence') || 'Driver licence');
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b-2 bg-gray-50">
            <th className="px-4 py-3 text-left font-medium text-gray-700">{t('labels.lblDocumentName')}</th>
            <th className="px-4 py-3 text-left font-medium text-gray-700">{t('labels.lblMessageStatus')}</th>
            <th className="px-4 py-3 text-left font-medium text-gray-700">{t('labels.lblType')}</th>
            <th className="px-4 py-3 text-left font-medium text-gray-700">{t('labels.lblRole')}</th>
            <th className="px-4 py-3 text-left font-medium text-gray-700">{t('labels.lblDatePosted')}</th>
            <th className="px-4 py-3 text-left font-medium text-gray-700">{t('labels.lblStatus')}</th>
            <th className="px-4 py-3 text-left font-medium text-gray-700">{t('labels.lblActions')}</th>
          </tr>
        </thead>
        <tbody>
          {documents && documents?.shareholders?.length > 0 ? (
            documents.shareholders?.map((shareholder: Shareholder, index: number) => (
              <React.Fragment key={index}>
                {/* Documentos do shareholder */}
                {shareholder?.documents?.map((document: any, docIndex: number) => (
                  <tr key={docIndex} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {getDocumentIcon(document.type)}
                        <span className="font-medium text-gray-800">{formatDocumentDescription(document.description, shareholder.name)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {document.message?.length > 32 ? `${document.message.substring(0, 32)}...` : document.message}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-800">{document.type}</td>
                    <td className="px-4 py-3 text-sm text-gray-800">
                      {shareholder.is_director ? t('labels.lblDirector') : t('labels.lblShareholder')}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDate(document.date_posted)}</td>
                    <td className="px-4 py-3">{getStatusBadge(document.status)}</td>
                    <td className="px-4 py-3">
                      <button
                        className="flex items-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg text-blue-700 font-medium transition-colors text-sm"
                        onClick={() => changeOpen(shareholder.slug, document.slug, document.document_url)}
                      >
                        <FiExternalLink className="w-4 h-4" />
                        {t('others.othViewDocument')}
                      </button>
                    </td>
                  </tr>
                ))}
                
                {/* Passaportes */}
                {shareholder?.passports?.map((document: any, docIndex: number) => (
                  <tr key={docIndex} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {getDocumentIcon(document.type)}
                        <span className="font-medium text-gray-800">
                          {shareholder.name} passport
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {document.message?.length > 32 ? `${document.message.substring(0, 32)}...` : document.message}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-800">{document.type}</td>
                    <td className="px-4 py-3 text-sm text-gray-800">
                      {shareholder.is_director ? t('labels.lblDirector') : t('labels.lblShareholder')}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDate(document.date_posted)}</td>
                    <td className="px-4 py-3">{getStatusBadge(document.status)}</td>
                    <td className="px-4 py-3">
                      <button
                        className="flex items-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg text-blue-700 font-medium transition-colors text-sm"
                        onClick={() => changeOpen(shareholder.slug, document.slug, document.document_url)}
                      >
                        <FiExternalLink className="w-4 h-4" />
                        {t('others.othViewDocument')}
                      </button>
                    </td>
                  </tr>
                ))}
                
                {/* Endereços */}
                {shareholder?.address?.map((document: any, docIndex: number) => (
                  <tr key={docIndex} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {getDocumentIcon(document.type)}
                        <span className="font-medium text-gray-800">
                          {shareholder.name} address
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {document.message?.length > 32 ? `${document.message.substring(0, 32)}...` : document.message}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-800">{document.type}</td>
                    <td className="px-4 py-3 text-sm text-gray-800">
                      {shareholder.is_director ? t('labels.lblDirector') : t('labels.lblShareholder')}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDate(document.date_posted)}</td>
                    <td className="px-4 py-3">{getStatusBadge(document.status)}</td>
                    <td className="px-4 py-3">
                      <button
                        className="flex items-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg text-blue-700 font-medium transition-colors text-sm"
                        onClick={() => changeOpen(shareholder.slug, document.slug, document.document_url)}
                      >
                        <FiExternalLink className="w-4 h-4" />
                        {t('others.othViewDocument')}
                      </button>
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))
          ) : null}
          
          {/* Documentos da empresa */}
          {documents?.company_documents?.map((company: any, docIndex: number) => (
            <tr key={docIndex} className="border-b hover:bg-gray-50">
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  {getDocumentIcon(company.document?.type)}
                  <span className="font-medium text-gray-800">
                    {company.description !== 'certificate_incorporation'
                      ? t('companyTransfer.descriptionText')
                      : company.description}
                  </span>
                </div>
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {company.document?.message?.length > 32 
                  ? `${company.document.message.substring(0, 32)}...` 
                  : company.document?.message}
              </td>
              <td className="px-4 py-3 text-sm text-gray-800">{company.document?.type}</td>
              <td className="px-4 py-3 text-sm text-gray-800">{t('others.othCompany')}</td>
              <td className="px-4 py-3 text-sm text-gray-600">{formatDate(company.document?.date_posted)}</td>
              <td className="px-4 py-3">{getStatusBadge(company.document?.status)}</td>
              <td className="px-4 py-3">
                <button
                  className="flex items-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg text-blue-700 font-medium transition-colors text-sm"
                  onClick={() => changeOpen('company', company.id, company.document?.document_url)}
                >
                  <FiExternalLink className="w-4 h-4" />
                  {t('others.othViewDocument')}
                </button>
              </td>
            </tr>
          ))}

          {/* Estado vazio */}
          {(!documents?.shareholders?.length && !documents?.company_documents?.length) && (
            <tr>
              <td colSpan={7} className="px-4 py-12 text-center">
                <div className="flex flex-col items-center">
                  <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">{t('titles.ttlThereNoRecords')}</h3>
                  <p className="text-gray-600">{t('labels.lblNoDocuments')}</p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
export { DocumentsList };
